import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
} from '@material-ui/core';
import {
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { CSVLink } from 'react-csv';
import AffiliateLayout from '../../components/affiliate/AffiliateLayoutSimple';
import ConversionsTable from '../../components/affiliate/ConversionsTable';
import { useQuery } from '@apollo/client';
import { AFFILIATE_CONVERSIONS_QUERY, CURRENT_AFFILIATE_QUERY } from '../../core/graphql/queries/affiliate';

const useStyles = makeStyles((theme) => ({
  headerCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #333',
    borderRadius: 8,
    marginBottom: theme.spacing(3),
  },
  headerContent: {
    padding: theme.spacing(3),
  },
  headerTitle: {
    color: '#fff',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  headerSubtitle: {
    color: '#ccc',
    marginBottom: theme.spacing(2),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  actionButton: {
    backgroundColor: '#2d4a3e',
    border: '1px solid #4caf50',
    color: '#4caf50',
    '&:hover': {
      backgroundColor: '#4caf50',
      color: '#fff',
    },
  },
  conversionsCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #333',
    borderRadius: 8,
    marginBottom: theme.spacing(3),
  },
  conversionsContent: {
    padding: theme.spacing(3),
  },
  detailDialog: {
    '& .MuiDialog-paper': {
      backgroundColor: '#1a2332',
      border: '1px solid #333',
    },
  },
  detailTitle: {
    color: '#fff',
    borderBottom: '1px solid #333',
    paddingBottom: theme.spacing(2),
  },
  detailContent: {
    color: '#ccc',
    padding: theme.spacing(3),
  },
  detailGrid: {
    marginBottom: theme.spacing(2),
  },
  detailLabel: {
    color: '#4caf50',
    fontWeight: 'bold',
  },
  detailValue: {
    color: '#fff',
  },
  statusChip: {
    fontWeight: 'bold',
  },
  pending: {
    backgroundColor: '#ff9800',
    color: '#fff',
  },
  approved: {
    backgroundColor: '#4caf50',
    color: '#fff',
  },
  paid: {
    backgroundColor: '#2196f3',
    color: '#fff',
  },
}));

interface Conversion {
  id: string;
  affiliateId: string;
  referralCode: string;
  artistId: string;
  artist: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  commissionAmount: number;
  status: 'pending' | 'approved' | 'paid' | 'rejected';
  approvedAt?: string;
  paidAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

const AffiliateConversions: React.FC = () => {
  const classes = useStyles();
  const [selectedConversion, setSelectedConversion] = useState<Conversion | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // First, get the current affiliate
  const { data: affiliateData, loading: affiliateLoading, error: affiliateError } = useQuery(CURRENT_AFFILIATE_QUERY, {
    errorPolicy: 'all',
  });

  const affiliate = affiliateData?.myAffiliate;

  // Then, get conversions for this affiliate
  const { data, loading, error } = useQuery(AFFILIATE_CONVERSIONS_QUERY, {
    variables: affiliate?.id ? {
      affiliateId: affiliate.id,
      limit: 1000, // Get all conversions for export
      offset: 0,
    } : {
      limit: 1000, // Get all conversions for export
      offset: 0,
    },
    skip: false, // Always run the query, even without affiliate ID
    errorPolicy: 'all',
  });

  const conversions = data?.conversions || [];

  // Handle loading state
  if (affiliateLoading || loading) {
    return (
      <AffiliateLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" style={{ color: '#fff' }}>
            Loading conversions...
          </Typography>
        </Box>
      </AffiliateLayout>
    );
  }

  // Handle error state - check affiliate errors first
  if (affiliateError) {
    const isNotAuthenticated = affiliateError.message?.includes('Not authenticated') || 
                              affiliateError.graphQLErrors?.some(e => e.message?.includes('Not authenticated'));
    
    if (isNotAuthenticated) {
      return (
        <AffiliateLayout>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Card style={{ backgroundColor: '#1a2332', border: '1px solid #333', padding: '2rem', textAlign: 'center' }}>
              <Typography variant="h5" style={{ color: '#fff', marginBottom: '1rem' }}>
                Not an Affiliate
              </Typography>
              <Typography style={{ color: '#ccc', marginBottom: '2rem' }}>
                You need to be an affiliate to view conversions. Please contact support to become an affiliate.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </Card>
          </Box>
        </AffiliateLayout>
      );
    }
  }

  // Show conversions even if not an affiliate (for testing purposes)
  // If no affiliate but conversions exist, show them anyway

  // Handle conversions error state
  if (error) {
    return (
      <AffiliateLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Card style={{ backgroundColor: '#1a2332', border: '1px solid #333', padding: '2rem', textAlign: 'center' }}>
            <Typography variant="h5" style={{ color: '#fff', marginBottom: '1rem' }}>
              Error Loading Conversions
            </Typography>
            <Typography style={{ color: '#ccc', marginBottom: '2rem' }}>
              Unable to load conversions data. Please try again later.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </Card>
        </Box>
      </AffiliateLayout>
    );
  }

  const handleViewConversion = (conversion: Conversion) => {
    setSelectedConversion(conversion);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedConversion(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      pending: { label: 'Pending', className: classes.pending },
      approved: { label: 'Approved', className: classes.approved },
      paid: { label: 'Paid', className: classes.paid },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <Chip
        label={config.label}
        className={`${classes.statusChip} ${config.className}`}
        size="small"
      />
    );
  };

  // Prepare CSV data
  const csvData = conversions.map((conversion: Conversion) => ({
    'Artist Name': conversion.artist?.firstName && conversion.artist?.lastName 
      ? `${conversion.artist.firstName} ${conversion.artist.lastName}`
      : conversion.artist?.email || 'N/A',
    'Sign Up Date': formatDate(conversion.createdAt),
    'Approval Date': conversion.approvedAt ? formatDate(conversion.approvedAt) : 'N/A',
    'Payment Date': conversion.paidAt ? formatDate(conversion.paidAt) : 'N/A',
    'Commission Amount': formatCurrency(conversion.commissionAmount),
    'Status': conversion.status,
    'Total Revenue': formatCurrency(conversion.commissionAmount),
  }));

  if (loading) {
    return (
      <AffiliateLayout title="Conversions" subtitle="Track your referral conversions and commissions.">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <Typography style={{ color: '#fff' }}>Loading conversions...</Typography>
        </Box>
      </AffiliateLayout>
    );
  }

  if (error) {
    return (
      <AffiliateLayout title="Conversions" subtitle="Track your referral conversions and commissions.">
        <Box textAlign="center" padding={4}>
          <Typography variant="h6" style={{ color: '#f44336' }} gutterBottom>
            Error Loading Conversions
          </Typography>
          <Typography variant="body2" style={{ color: '#ccc' }}>
            Unable to load conversions data. Please try again later.
          </Typography>
        </Box>
      </AffiliateLayout>
    );
  }

  return (
    <AffiliateLayout
      title="Conversions"
      subtitle="Track your referral conversions and commissions."
    >
      {/* Header with Actions */}
      <Card className={classes.headerCard}>
        <CardContent className={classes.headerContent}>
          <Typography variant="h5" className={classes.headerTitle}>
            Conversion History
          </Typography>
          <Typography variant="body1" className={classes.headerSubtitle}>
            View and export your referral conversions. Track commission status and earnings.
          </Typography>
          <Box className={classes.actionButtons}>
            <CSVLink
              data={csvData}
              filename={`affiliate-conversions-${new Date().toISOString().split('T')[0]}.csv`}
              style={{ textDecoration: 'none' }}
            >
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                className={classes.actionButton}
              >
                Export CSV
              </Button>
            </CSVLink>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              className={classes.actionButton}
            >
              Advanced Filters
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Conversions Table */}
      <Card className={classes.conversionsCard}>
        <CardContent className={classes.conversionsContent}>
          <ConversionsTable onViewConversion={handleViewConversion} />
        </CardContent>
      </Card>

      {/* Conversion Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
        className={classes.detailDialog}
      >
        <DialogTitle className={classes.detailTitle}>
          Conversion Details
        </DialogTitle>
        <DialogContent className={classes.detailContent}>
          {selectedConversion && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} className={classes.detailGrid}>
                <Typography variant="body2" className={classes.detailLabel}>
                  Artist Name
                </Typography>
                <Typography variant="body1" className={classes.detailValue}>
                  {selectedConversion.artist?.firstName && selectedConversion.artist?.lastName 
                    ? `${selectedConversion.artist.firstName} ${selectedConversion.artist.lastName}`
                    : selectedConversion.artist?.email || 'N/A'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6} className={classes.detailGrid}>
                <Typography variant="body2" className={classes.detailLabel}>
                  Status
                </Typography>
                {getStatusChip(selectedConversion.status)}
              </Grid>
              
              <Grid item xs={12} sm={6} className={classes.detailGrid}>
                <Typography variant="body2" className={classes.detailLabel}>
                  Sign Up Date
                </Typography>
                <Typography variant="body1" className={classes.detailValue}>
                  {formatDate(selectedConversion.createdAt)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6} className={classes.detailGrid}>
                <Typography variant="body2" className={classes.detailLabel}>
                  Approval Date
                </Typography>
                <Typography variant="body1" className={classes.detailValue}>
                  {selectedConversion.approvedAt ? formatDate(selectedConversion.approvedAt) : 'Not approved yet'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6} className={classes.detailGrid}>
                <Typography variant="body2" className={classes.detailLabel}>
                  Payment Date
                </Typography>
                <Typography variant="body1" className={classes.detailValue}>
                  {selectedConversion.paidAt ? formatDate(selectedConversion.paidAt) : 'Not paid yet'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6} className={classes.detailGrid}>
                <Typography variant="body2" className={classes.detailLabel}>
                  Commission Amount
                </Typography>
                <Typography variant="body1" className={classes.detailValue}>
                  {formatCurrency(selectedConversion.commissionAmount)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} className={classes.detailGrid}>
                <Typography variant="body2" className={classes.detailLabel}>
                  Total Revenue Generated
                </Typography>
                <Typography variant="body1" className={classes.detailValue}>
                  {formatCurrency(selectedConversion.commissionAmount)}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog} className={classes.actionButton}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </AffiliateLayout>
  );
};

export default AffiliateConversions;
